import { useState } from 'react';
import { removeEmptyValuesFromObject, filterDefaultValues } from 'utils/object';
import { startOfMonth, subMonths } from 'date-fns';
import { assigneeOptions } from 'constants/forms';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';

import { useQueryParams } from './useQueryParams';

import type { SortState } from 'infrastructure/types';
import type { Filters, AppendFilters, SortString, SortField } from './types';

export const splitSort = <T extends SortString>(
  s: T | (string & {}),
): [SortField, SortState] => {
  return s.split(':') as [SortField, SortState];
};

type TQuerySortString = SortString | null | undefined;

export const getColSortDir = (sortField: SortField) => {
  const queryFilter =
    new URLSearchParams(window.location.search).get('filters') ?? '{}';
  const querySortString = JSON.parse(queryFilter)
    ?.sortString as TQuerySortString;

  if (querySortString) {
    const [field, dir] = splitSort(querySortString);
    if (sortField === field) return tableSortMap[dir];
  }

  return null;
};

export const getFiltersCount = (filters: Record<string, any>): number => {
  const { page: _, items: __, ...rest } = filters;
  let count = Object.keys(rest).length;
  // this two filters are count as one filter, so we need to remove 1 from total filters count
  if ('interactionFlagStartTime' in rest && 'interactionFlagEndTime' in rest) {
    count -= 1;
  }
  // this two filters are count as one filter, so we need to remove 1 from total filters count
  if ('year' in rest && 'month' in rest) {
    count -= 1;
  }
  // this two filters are count as one filter, so we need to remove 1 from total filters count
  if ('status' in rest && 'ccmStatus' in rest) {
    count -= 1;
  }

  if ('sortString' in rest) {
    count -= 1;
  }
  return count;
};

export const useInteractiveTimeDashboardFilters = (
  defaultFilters?: Partial<Filters>,
) => {
  const currentDate = new Date();
  const lastReadingEndDate = startOfMonth(subMonths(new Date(), 3));

  const DEFAULT_FILTERS = {
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
    lastReadingStartTime: lastReadingEndDate.getTime(),
    page: 1,
    items: 100,
    assignee: assigneeOptions[1],
    ...defaultFilters,
  };

  const {
    addFiltersToUrl,
    initialFilters,
    initialFiltersCount,
    resetFiltersUrl,
  } = useQueryParams(DEFAULT_FILTERS);

  const [filtersCount, setFiltersCount] = useState<number>(initialFiltersCount);
  const [filters, setFilters] = useState<Filters>(initialFilters);

  const appendFilters: AppendFilters = (filtersToAppend) => {
    const newFilters = removeEmptyValuesFromObject({
      ...DEFAULT_FILTERS,
      ...filters,
      page: 1,
      ...filtersToAppend,
    });

    const filterWithoutDefaultValues = filterDefaultValues({
      values: newFilters,
      defaultValues: DEFAULT_FILTERS,
    });

    addFiltersToUrl(filterWithoutDefaultValues);
    const newFiltersCount = getFiltersCount(filterWithoutDefaultValues);
    setFiltersCount(newFiltersCount);
    setFilters(newFilters as Filters);
  };

  const changePage = (page: number) => {
    appendFilters({ page });
  };

  const resetFilters = () => {
    setFiltersCount(0);
    setFilters(DEFAULT_FILTERS);
    resetFiltersUrl();
  };

  return {
    filters,
    appendFilters,
    resetFilters,
    changePage,
    filtersCount,
  };
};
